import {
  ArrowsClockwiseIcon,
  CheckCircle,
  Copy,
  Pulse,
  Trash,
  WarningCircle,
  Waveform
} from "@phosphor-icons/react";
import {
  type ReactNode,
  useEffect,
  useMemo,
  useState
} from "react";
import { useObservations } from "./react.tsx";
import { emptyObservationInput } from "./model.ts";
import {
  ResourceTreeAction,
  ResourceTreeRow
} from "../ResourceExplorerTree";
import type {
  TraceSpanRecord,
  TraceStats,
  TraceSummary
} from "@workerstation/shared";

interface TraceExplorerProps {
  stationId: string | null;
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  refreshToken?: number;
  onCollapsedChange: (collapsed: boolean) => void;
}

const EMPTY_STATS: TraceStats = {
  traceCount: 0,
  spanCount: 0,
  errorCount: 0,
  averageDurationMs: 0,
  p95DurationMs: 0,
  topOperations: []
};

function duration(value: number): string {
  if (value < 1) return `${value.toFixed(2)} ms`;
  if (value < 1_000) return `${value.toFixed(value < 10 ? 1 : 0)} ms`;
  return `${(value / 1_000).toFixed(2)} s`;
}

function shortTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
}

function orderedSpans(spans: readonly TraceSpanRecord[]): TraceSpanRecord[] {
  const byId = new Map(spans.map((span) => [span.id, span]));
  const children = new Map<string, TraceSpanRecord[]>();
  const roots: TraceSpanRecord[] = [];
  for (const span of spans) {
    if (!span.parentId || !byId.has(span.parentId)) {
      roots.push(span);
      continue;
    }
    const siblings = children.get(span.parentId) ?? [];
    siblings.push(span);
    children.set(span.parentId, siblings);
  }
  const ordered: TraceSpanRecord[] = [];
  const visit = (span: TraceSpanRecord) => {
    ordered.push(span);
    for (const child of (children.get(span.id) ?? []).sort(
      (left, right) => left.startedAt - right.startedAt
    )) {
      visit(child);
    }
  };
  for (const root of roots.sort((left, right) => left.startedAt - right.startedAt)) {
    visit(root);
  }
  return ordered;
}

function spanDepth(span: TraceSpanRecord, byId: Map<string, TraceSpanRecord>) {
  let depth = 0;
  let parentId = span.parentId;
  const visited = new Set<string>();
  while (parentId && depth < 8 && !visited.has(parentId)) {
    visited.add(parentId);
    depth += 1;
    parentId = byId.get(parentId)?.parentId;
  }
  return depth;
}

function StatusIcon({ status }: { status: "ok" | "error" }) {
  return status === "error" ? (
    <WarningCircle size={14} weight="fill" className="text-red-500" />
  ) : (
    <CheckCircle size={14} weight="fill" className="text-emerald-500" />
  );
}

function SpanDetails({ span }: { span: TraceSpanRecord }) {
  const detail = {
    id: span.id,
    traceId: span.traceId,
    parentId: span.parentId,
    component: span.component,
    operation: span.operation,
    kind: span.kind,
    status: span.status,
    durationMs: span.durationMs,
    startedAt: new Date(span.startedAt).toISOString(),
    endedAt: new Date(span.endedAt).toISOString(),
    attributes: span.attributes,
    input: span.input,
    output: span.output,
    error: span.error
  };
  return (
    <pre className="mx-2 mb-1 max-h-64 overflow-auto rounded border border-kumo-line bg-kumo-elevated/40 p-2 text-[9px] leading-4 text-kumo-subtle">
      {JSON.stringify(detail, null, 2)}
    </pre>
  );
}

export function TraceExplorer({
  stationId,
  workspaceId,
  connectionName,
  collapsed,
  headerLeading,
  refreshToken,
  onCollapsedChange
}: TraceExplorerProps) {
  const [selectedSpan, setSelectedSpan] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "ok" | "error">("all");
  const [live, setLive] = useState(false);
  const owned=useObservations({...emptyObservationInput("traces"),workspaceId,stationId,collapsed,refreshToken:refreshToken??0,search,status,live});
  const traces=owned.snapshot.traces?.traces??[],stats=owned.snapshot.traces?.stats??EMPTY_STATS;
  const {details,loading,error}=owned.snapshot,expanded=new Set(owned.snapshot.expanded);
  useEffect(()=>{setSelectedSpan(null);},[workspaceId,stationId,owned.snapshot.clearVersion]);
  const refresh=()=>owned.dispatch({_tag:"Refresh"});
  const clear=()=>owned.dispatch({_tag:"Clear"});
  const toggleTrace=(trace:TraceSummary)=>{
    const spans=Object.hasOwn(details,trace.traceId)?details[trace.traceId]:[];
    if(expanded.has(trace.traceId)&&spans.some(span=>span.id===selectedSpan))setSelectedSpan(null);
    owned.dispatch({_tag:"ToggleTrace",traceId:trace.traceId});
  };
  const copyTrace=(traceId:string)=>owned.dispatch({_tag:"CopyTrace",traceId});

  const selected = useMemo(() => {
    if (!selectedSpan) return undefined;
    return Object.values(details)
      .flat()
      .find((span) => span.id === selectedSpan);
  }, [details, selectedSpan]);

  return (
    <section className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line">
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            <Pulse size={12} /> Automatic traces
          </button>
        )}
        <button
          type="button"
          aria-pressed={live}
          title={live ? "Disable live trace refresh" : "Enable live trace refresh"}
          onClick={() => setLive((value) => !value)}
          className={`mr-0.5 h-4 rounded px-1 text-[8px] font-semibold uppercase ${
            live
              ? "bg-emerald-500/15 text-emerald-500"
              : "bg-kumo-elevated text-kumo-subtle"
          }`}
        >
          live
        </button>
        <ResourceTreeAction
          label="Refresh automatic traces"
          onClick={() => void refresh()}
          disabled={!workspaceId || loading}
        >
          <ArrowsClockwiseIcon
            size={13}
            className={loading ? "animate-spin" : ""}
          />
        </ResourceTreeAction>
        <ResourceTreeAction
          label="Clear automatic traces"
          onClick={() => void clear()}
          disabled={!workspaceId || loading}
        >
          <Trash size={13} />
        </ResourceTreeAction>
      </div>

      {!collapsed ? (
        <>
          <div className="flex shrink-0 gap-1 border-b border-kumo-line/60 p-1">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter operation…"
              aria-label="Filter trace operations"
              className="h-6 min-w-0 flex-1 rounded border border-kumo-line bg-kumo-base px-1.5 text-[10px] text-kumo-default outline-none focus:border-kumo-accent"
            />
            <select
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as "all" | "ok" | "error")
              }
              aria-label="Filter trace status"
              className="h-6 rounded border border-kumo-line bg-kumo-base px-1 text-[10px] text-kumo-default"
            >
              <option value="all">all</option>
              <option value="ok">ok</option>
              <option value="error">error</option>
            </select>
          </div>
          <div className="grid shrink-0 grid-cols-3 gap-px border-b border-kumo-line/60 bg-kumo-line/60 text-center text-[9px]">
            <div className="bg-kumo-base px-1 py-1 text-kumo-subtle">
              <strong className="block text-xs text-kumo-default">{stats.traceCount}</strong>
              traces
            </div>
            <div className="bg-kumo-base px-1 py-1 text-kumo-subtle">
              <strong className="block text-xs text-kumo-default">{stats.spanCount}</strong>
              spans
            </div>
            <div className="bg-kumo-base px-1 py-1 text-kumo-subtle">
              <strong className={stats.errorCount ? "block text-xs text-red-500" : "block text-xs text-kumo-default"}>
                {stats.errorCount}
              </strong>
              errors
            </div>
          </div>
          <div role="tree" className="min-h-0 flex-1 overflow-auto py-1">
            <ResourceTreeRow
              depth={0}
              label={connectionName || "Workerspace"}
              detail={`p95 ${duration(stats.p95DurationMs)}`}
              icon={<Waveform size={14} />}
            />
            {traces.map((trace) => {
              const traceSpans = orderedSpans(Object.hasOwn(details,trace.traceId) ? details[trace.traceId] : []);
              const byId = new Map(traceSpans.map((span) => [span.id, span]));
              const isExpanded = expanded.has(trace.traceId);
              return (
                <div key={trace.traceId} role="group">
                  <ResourceTreeRow
                    depth={1}
                    label={trace.rootOperation}
                    detail={`${duration(trace.durationMs)} · ${trace.spanCount}${trace.truncated ? "+" : ""} spans · ${shortTime(trace.startedAt)}`}
                    icon={<StatusIcon status={trace.status} />}
                    expandable
                    expanded={isExpanded}
                    onClick={() => void toggleTrace(trace)}
                    title={`${trace.components.join(", ")}\n${trace.traceId}`}
                    actions={
                      <ResourceTreeAction
                        label={`Copy trace ${trace.traceId}`}
                        onClick={() => void copyTrace(trace.traceId)}
                        disabled={!traceSpans.length}
                      >
                        <Copy size={12} />
                      </ResourceTreeAction>
                    }
                  />
                  {isExpanded
                    ? traceSpans.map((span) => (
                        <div key={span.id}>
                          <ResourceTreeRow
                            depth={2 + spanDepth(span, byId)}
                            label={span.operation}
                            detail={`${duration(span.durationMs)} · ${span.kind}`}
                            icon={<StatusIcon status={span.status} />}
                            onClick={() =>
                              setSelectedSpan((current) =>
                                current === span.id ? null : span.id
                              )
                            }
                            title={`${span.component}\n${span.id}`}
                          />
                          {selected?.id === span.id ? (
                            <SpanDetails span={span} />
                          ) : null}
                        </div>
                      ))
                    : null}
                </div>
              );
            })}
            {!traces.length && !loading ? (
              <p className="px-3 py-3 text-[10px] text-kumo-subtle">
                No traces yet. Run Preview, Test, a resource operation, or an MCP
                tool to capture automatic spans.
              </p>
            ) : null}
            {error ? (
              <p className="px-3 py-2 text-[10px] text-red-500">{error}</p>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
}
